import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Divider,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import apiClient from '../utils/api';
import { AdminLayout } from '../components/AdminLayout';

interface ResponseTimeLog {
  id: string;
  room_number: number | null;
  project_no: string | null;
  customer_replied_at: string;
  operator_replied_at: string;
  operator_name: string;
  elapsed_minutes: number;
  elapsed_display: string;
  room_title: string | null;
}

interface Summary {
  total: number;
  avg_minutes: number;
  avg_display: string;
  max_minutes: number;
  max_display: string;
  min_minutes: number;
  min_display: string;
}

interface WeeklyReport {
  id: string;
  week_start: string;
  week_end: string;
  generated_at: string;
  total_records: number;
  avg_elapsed_minutes: string;
  max_elapsed_minutes: number;
  min_elapsed_minutes: number;
  avg_display: string;
  max_display: string;
  min_display: string;
}

function formatJSTDateTime(dt: string | null): string {
  if (!dt) return '-';
  const d = new Date(dt);
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

function getElapsedColor(minutes: number): string {
  if (minutes <= 30) return '#4caf50';
  if (minutes <= 60) return '#ff9800';
  if (minutes <= 120) return '#f44336';
  return '#b71c1c';
}

const ResponseTimeReport: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [logs, setLogs] = useState<ResponseTimeLog[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [weeklyReports, setWeeklyReports] = useState<WeeklyReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // 今週月〜日のデフォルト範囲
  useEffect(() => {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMon);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    setStartDate(monday.toISOString().slice(0, 10));
    setEndDate(sunday.toISOString().slice(0, 10));
  }, []);

  const fetchLogs = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const data = await apiClient.getResponseTimeLogs({ startDate, endDate });
      if (data.success) {
        setLogs(data.logs);
        setSummary(data.summary);
      }
    } catch (error) {
      console.error('ログ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const fetchWeeklyReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.getWeeklyResponseReports();
      if (data.success) {
        setWeeklyReports(data.reports);
      }
    } catch (error) {
      console.error('週次レポート取得エラー:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabIndex === 0 && startDate && endDate) {
      fetchLogs();
    } else if (tabIndex === 1) {
      fetchWeeklyReports();
    }
  }, [tabIndex, fetchLogs, fetchWeeklyReports, startDate, endDate]);

  const handleDownloadCSV = async () => {
    try {
      await apiClient.downloadResponseTimeCSV({ startDate, endDate });
      setSnackbar({ open: true, message: 'CSVをダウンロードしました', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'CSVダウンロードに失敗しました', severity: 'error' });
    }
  };

  const handleDownloadWeeklyCSV = async (reportId: string) => {
    try {
      await apiClient.downloadWeeklyResponseCSV(reportId);
      setSnackbar({ open: true, message: '週次CSVをダウンロードしました', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'CSVダウンロードに失敗しました', severity: 'error' });
    }
  };

  const handleGenerateWeekly = async () => {
    try {
      const data = await apiClient.triggerWeeklyResponseReport();
      if (data.success) {
        setSnackbar({ open: true, message: '週次レポートを生成しました', severity: 'success' });
        fetchWeeklyReports();
      }
    } catch {
      setSnackbar({ open: true, message: '週次レポート生成に失敗しました', severity: 'error' });
    }
  };

  return (
    <AdminLayout>
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <SpeedIcon sx={{ fontSize: 28, color: '#1976d2' }} />
        <Typography variant="h5" fontWeight="bold">
          レスポンスタイムレポート
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        顧客返信 → オペレーター返信の経過時間を記録。営業時間（毎日10:00〜19:00、祝日除く）でカウントされます。
      </Alert>

      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
        <Tab label="ログ一覧" icon={<TimeIcon />} iconPosition="start" />
        <Tab label="週次レポート" icon={<TrendingUpIcon />} iconPosition="start" />
      </Tabs>

      {tabIndex === 0 && (
        <>
          {/* フィルター */}
          <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="開始日"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="終了日"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" size="small" onClick={fetchLogs} startIcon={<RefreshIcon />}>
              検索
            </Button>
            <Button variant="outlined" size="small" onClick={handleDownloadCSV} startIcon={<DownloadIcon />}>
              CSV
            </Button>
          </Paper>

          {/* サマリー */}
          {summary && summary.total > 0 && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">件数</Typography>
                <Typography variant="h5" fontWeight="bold">{summary.total}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">平均</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">{summary.avg_display}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">最大</Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: getElapsedColor(summary.max_minutes) }}>{summary.max_display}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">最小</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">{summary.min_display}</Typography>
              </Paper>
            </Box>
          )}

          {/* テーブル */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>案件No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>顧客返信日時</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>オペレーター返信日時</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>オペレーター</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>経過時間</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        該当期間のデータがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                          {log.room_number || '-'}
                        </TableCell>
                        <TableCell>{log.project_no || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {formatJSTDateTime(log.customer_replied_at)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {formatJSTDateTime(log.operator_replied_at)}
                        </TableCell>
                        <TableCell>{log.operator_name || '-'}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={log.elapsed_display}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              backgroundColor: getElapsedColor(log.elapsed_minutes) + '18',
                              color: getElapsedColor(log.elapsed_minutes),
                              border: `1px solid ${getElapsedColor(log.elapsed_minutes)}40`,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tabIndex === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" size="small" onClick={handleGenerateWeekly} startIcon={<RefreshIcon />}>
              前週レポート生成
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>対象期間</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>件数</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>平均</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>最大</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>最小</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>生成日時</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>CSV</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklyReports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        週次レポートがまだありません。「前週レポート生成」ボタンで作成できます。
                      </TableCell>
                    </TableRow>
                  ) : (
                    weeklyReports.map((report) => (
                      <TableRow key={report.id} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {report.week_start} 〜 {report.week_end}
                        </TableCell>
                        <TableCell align="right">{report.total_records}件</TableCell>
                        <TableCell align="right">
                          <Chip label={report.avg_display} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{report.max_display}</TableCell>
                        <TableCell align="right">{report.min_display}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {formatJSTDateTime(report.generated_at)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="CSVダウンロード">
                            <IconButton size="small" onClick={() => handleDownloadWeeklyCSV(report.id)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        営業時間: 毎日 10:00〜19:00（祝日を除く）/ 経過時間は営業時間のみカウント
      </Typography>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
    </AdminLayout>
  );
};

export default ResponseTimeReport;
